import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

/**
 * MembershipContext: answers a single question cleanly: "is the current user
 * an active member of the ecosystem currently being viewed?"
 *
 * The signal is about the user × ecosystem pair, so this context sits between
 * AuthContext (the Firebase user) and EcosystemContext (the resolved tenant).
 * It fetches `GET /api/users/me` and reads the explicit, non-ambiguous
 * `currentEcosystemMembership` field the server adds for exactly this purpose,
 * NOT the merged mentor/affiliation fields, which cannot distinguish a
 * non-member from a member with an empty profile.
 *
 * State:
 *   - `membership`  : `{ role, status, joinedAt }` for the active ecosystem,
 *                     or `null` (no membership row, or platform mode).
 *   - `isMember`    : `true` only when `membership.status === 'active'`.
 *   - `loading`     : `true` until the membership for the CURRENT
 *                     (user, ecosystem) pair has been resolved. Derived from
 *                     a resolved-key comparison so it is correct *synchronously*
 *                     on the render where `firebaseUser` or `ecosystem`
 *                     changes: a route guard must never see a stale
 *                     "not loading / not a member" window and redirect on it.
 *   - `refresh()`   : re-fetch `/me`: call this after a join / leave so the
 *                     join banner and member-gated UI update without a
 *                     manual page reload.
 *
 * `fetch` is used directly (not the axios instance) to mirror AuthContext's
 * own `/me` probe and avoid a circular dependency through the axios layer.
 * The ecosystem `basePath` is prepended so the server's tenant-resolution
 * middleware resolves the active ecosystem in path mode.
 */
const MembershipContext = createContext();

export const MembershipProvider = ({ children }) => {
    const { user: firebaseUser } = useAuth();
    const { ecosystem, slug, basePath } = useEcosystem();

    const [membership, setMembership] = useState(null);
    // The (user, ecosystem) pair the membership state was last resolved for.
    // `loading` is DERIVED from this vs the current pair (below), so it is
    // correct synchronously on the render where an input changes, with no
    // lag window an irreversible route-guard redirect could act on.
    const [resolvedKey, setResolvedKey] = useState(null);

    // Monotonic generation counter: every call to `fetchMembership` claims
    // the next generation and, on resolution, writes to state ONLY if its
    // generation is still the latest one issued. Without this guard a slow
    // fetch resolving AFTER a faster newer fetch would overwrite the newer
    // state with stale `(user, ecosystem)` data: `loading` would then read
    // `false` while `membership` belongs to a previous pair, and route
    // guards (EcosystemAdminRoute, JoinEcosystemBanner) would decide on
    // stale state (review finding 15).
    const fetchGenRef = useRef(0);

    // A stable key for the current user × ecosystem pair, or 'none' when
    // there is no pair to evaluate (platform mode / signed out / unverified).
    const currentKey =
        firebaseUser && firebaseUser.emailVerified && ecosystem
            ? `${firebaseUser.uid}::${ecosystem._id}`
            : 'none';

    const fetchMembership = useCallback(async () => {
        // Claim a generation for this fetch. The closure captures it; we
        // only commit state if no later fetch has superseded us.
        const myGen = ++fetchGenRef.current;
        const keyAtStart = currentKey;

        // Platform mode (no ecosystem) or no verified user: there is no
        // user × ecosystem pair to evaluate.
        if (currentKey === 'none') {
            if (myGen === fetchGenRef.current) {
                setMembership(null);
                setResolvedKey('none');
            }
            return;
        }

        let nextMembership = null;
        try {
            const token = await firebaseUser.getIdToken();
            const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
            // The tenant path prefix (`/costem` in path mode, '' in
            // subdomain/platform mode) MUST be included so the server's
            // tenant-resolution middleware resolves the active ecosystem.
            // otherwise `/me` runs in platform mode and always returns
            // `currentEcosystemMembership: null`.
            const res = await fetch(`${baseUrl}${basePath}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                nextMembership = data.currentEcosystemMembership ?? null;
            }
            // Non-ok response: 404 (no MongoDB profile yet) / 401 /
            // unexpected: falls through with nextMembership = null. Treat
            // as "not a member" rather than blocking the UI.
        } catch {
            // Network failure: fall back to "not a member" so the app stays
            // usable; the banner simply will not appear.
            nextMembership = null;
        }

        // Stale-response guard: discard if a newer fetch has been issued
        // OR if the input pair changed mid-flight. Either case means the
        // result this fetch was about to commit belongs to a prior
        // (user, ecosystem) pair and would corrupt downstream state.
        if (myGen !== fetchGenRef.current || keyAtStart !== currentKey) {
            return;
        }

        setMembership(nextMembership);
        setResolvedKey(currentKey);
    }, [currentKey, firebaseUser, basePath]);

    // Re-resolve whenever the user or the active ecosystem changes.
    useEffect(() => {
        fetchMembership();
    }, [fetchMembership]);

    // Derived loading: we are loading whenever the membership has not yet
    // been resolved for the current pair. Synchronously correct on input
    // changes: no stale window.
    const loading = resolvedKey !== currentKey;

    const value = useMemo(
        () => ({
            membership,
            isMember: membership?.status === 'active',
            // `role` is gated on the active membership status. Defense-in-
            // depth (round-2 review finding 9): the server `/me` endpoint
            // already filters out non-active rows, so `membership` is null
            // for `removed` / `pending`. But if a future server regression
            // ever surfaces a removed membership again, this client gate
            // ensures the role is not silently echoed back to admin UI
            // affordances. Pair with `isMember`: never expose `role`
            // without also confirming `isMember`.
            role:
                membership?.status === 'active'
                    ? (membership?.role ?? null)
                    : null,
            loading,
            slug,
            refresh: fetchMembership,
        }),
        [membership, loading, slug, fetchMembership]
    );

    return (
        <MembershipContext.Provider value={value}>
            {children}
        </MembershipContext.Provider>
    );
};

MembershipProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * Access the current user's membership state for the active ecosystem.
 *
 * @returns {Object} { membership, isMember, role, loading, slug, refresh }.
 * @throws {Error} If used outside of a MembershipProvider.
 */
export const useMembership = () => {
    const context = useContext(MembershipContext);
    if (context === undefined) {
        throw new Error(
            'useMembership must be used within a MembershipProvider'
        );
    }
    return context;
};

export default MembershipContext;
